/// <amd-module name="Repos/PullRequestActions/Components/ChecksPanel" />
import * as React from "react";
import { PolicyEvaluationStatus } from "Repos/Common/Contracts/Policy";
import * as Resources from "Repos/PullRequestActions/Resources";
import { addClickFPSToChecks, CheckActionButton } from "Repos/Views/PullRequest/Components/PullRequestDetails/CompletionStatus";
import { CheckEvaluation, ICompletionStatus } from "Repos/Views/PullRequest/Contracts/PullRequest";
import { getPullRequestPolicyService } from "Repos/Views/PullRequest/PullRequestPolicyService";
import { getPullRequestViewService } from "Repos/Views/PullRequest/PullRequestViewService";
import * as CheckUtils from "Repos/Views/PullRequest/Util/Check";
import { format } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { useComponentContext } from "VSS/Platform/Layout";
import { Icon } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelHeader } from "VSSUI/Panel";
import { Status, StatusSize } from "VSSUI/Status";
import { ITableColumn, SimpleTableCell, Table } from "VSSUI/Table";
import { Time } from "VSSUI/Time";
import { Tooltip } from "VSSUI/TooltipEx";
import { hour, minute, tooltipString } from "VSSUI/Utilities/Date";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

export interface ChecksPanelProps {
    onDismiss(): void;
}

export function ChecksPanel(props: ChecksPanelProps) {
    const { pageContext } = useComponentContext();
    const prPolicyService = getPullRequestPolicyService(pageContext);
    const prViewService = getPullRequestViewService(pageContext);

    const selectiveAutoCompleteEnabled = isFeatureFlagEnabled(pageContext, "SourceControl.GitPullRequests.SelectiveAutoComplete", false);

    return (
        <CustomPanel onDismiss={props.onDismiss}>
            <PanelHeader titleProps={{ text: Resources.Checks }} onDismiss={props.onDismiss} description={Resources.NumberOfDisplayedChecksIsLimited}  />
            <Observer checks={prPolicyService.getCheckEvaluations()} completionStatus={prViewService.getCompletionStatus()}>
                {({ checks, completionStatus }: { checks?: CheckEvaluation[]; completionStatus: ICompletionStatus }) => {
                    if (!checks) {
                        return null;
                    }

                    let topSectionChecks: CheckEvaluation[];

                    let bottomSectionChecks: CheckEvaluation[];

                    if (!completionStatus.autoCompletingData) {
                        // When auto-complete is not set, we show what's required versus optional for immediate completion.
                        topSectionChecks = checks.filter(check => check.isRequired);
                        bottomSectionChecks = checks.filter(check => !check.isRequired);
                    } else {
                        // When auto-complete is set, "Required" means "auto-complete is waiting for" and "Optional" means
                        // "auto-complete will ignore this"

                        // Find policies which autocomplete would block on which are not already in the ignored list
                        const blockingPolicyIds = new Set(completionStatus.autoCompletingData.blockingPolicyIds || []);

                        // Find policies which autocomplete would block on which are not already in the ignored list
                        const ignoredPolicyIds = new Set(
                            (completionStatus.autoCompletingData.completionOptions &&
                                completionStatus.autoCompletingData.completionOptions.autoCompleteIgnoreConfigIds) ||
                                []
                        );

                        const { blockingChecks, nonBlockingChecks } = CheckUtils.computeAutoCompleteBlockers(
                            checks,
                            blockingPolicyIds,
                            ignoredPolicyIds
                        );

                        topSectionChecks = blockingChecks;
                        bottomSectionChecks = nonBlockingChecks;
                    }

                    return (
                        <div className="flex-grow rhythm-vertical-16 v-scroll-auto custom-scrollbar">
                            {topSectionChecks.length > 0 && (
                                <div key="top">
                                    <div
                                        className="body-s font-weight-semibold secondary-text padding-vertical-8 bolt-default-horizontal-spacing"
                                        role="heading"
                                        aria-level={2}
                                    >
                                        {Resources.Required}
                                    </div>
                                    <ChecksTable ariaLabel={Resources.Required} checks={topSectionChecks} />
                                </div>
                            )}

                            {bottomSectionChecks.length > 0 && (
                                <div key="bottom">
                                    <div
                                        className="body-s font-weight-semibold secondary-text padding-vertical-8 bolt-default-horizontal-spacing"
                                        role="heading"
                                        aria-level={2}
                                    >
                                        {Resources.Optional}
                                        {completionStatus.autoCompletingData && <div>{Resources.IgnoredByAutoComplete}</div>}
                                    </div>
                                    <ChecksTable ariaLabel={Resources.Optional} checks={bottomSectionChecks} />
                                </div>
                            )}
                        </div>
                    );
                }}
            </Observer>
        </CustomPanel>
    );
}

const tableColumns: ITableColumn<CheckEvaluation>[] = [
    {
        id: "check",
        renderCell: (rowIndex, columnIndex, tableColumn, item) => {
            return (
                <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                    <div className="flex-row flex-grow flex-center rhythm-horizontal-16 text-ellipsis">
                        <Status {...item.statusProps} size={StatusSize.l} />
                        <div className="flex-column flex-grow scroll-hidden">
                            <Tooltip overflowOnly={true}>
                                <div className="font-weight-semibold text-ellipsis">
                                    {item.message}
                                    {item.isRequired && (
                                        <Icon
                                            iconName="Ribbon"
                                            className="font-weight-light v-align-middle padding-bottom-4 padding-left-8"
                                            tooltipProps={{ text: Resources.RequiredByPolicy }}
                                        />
                                    )}
                                </div>
                            </Tooltip>
                            <Tooltip overflowOnly={true}>
                                <div className="secondary-text text-ellipsis">
                                    {item.expirationDate && item.status === PolicyEvaluationStatus.Approved ? (
                                        <ExpirationTime expirationDate={item.expirationDate} />
                                    ) : (
                                        item.statusTitle
                                    )}
                                </div>
                            </Tooltip>
                        </div>
                        <CheckActionButton actions={item.getActions()} />
                    </div>
                </SimpleTableCell>
            );
        },
        width: -100
    }
];

function ChecksTable({ checks, ariaLabel }: { checks: CheckEvaluation[]; ariaLabel: string }) {
    const { pageContext } = useComponentContext();

    return (
        <Table
            ariaLabel={ariaLabel}
            columns={tableColumns}
            itemProvider={new ArrayItemProvider(addClickFPSToChecks(checks, pageContext))}
            singleClickActivation={false}
            showHeader={false}
            virtualize={false}
        />
    );
}

function ExpirationTime(props: { expirationDate: Date }) {
    const getTimeString = () => {
        const diff = props.expirationDate.getTime() - Date.now();
        if (diff < 0) {
            return Resources.Expired;
        } else if (diff < 59 * minute) {
            return format(Resources.ExpiresInMinutes, Math.round(diff / minute) || 1);
        } else {
            return format(Resources.ExpiresInHours, Math.round(diff / hour) || 1);
        }
    };

    const getNextInterval = () => {
        const diff = props.expirationDate.getTime() - Date.now();
        if (diff < 0) {
            return hour;
        } else if (diff < 59 * minute) {
            return diff % minute;
        } else {
            return diff % hour;
        }
    };

    return (
        <Time
            dateTime={props.expirationDate}
            getTimeString={getTimeString}
            getNextInterval={getNextInterval}
            tooltipProps={{
                renderContent: () => tooltipString(props.expirationDate)
            }}
        />
    );
}
